import { type ReactNode, useId } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { Switch } from 'react-aria-components';
import { Checkbox } from '../tailgrids/core/checkbox.js';
import { cn } from '../../utils/cn.js';
import { HelpIcon } from '../ui/HelpIcon.js';
import type { FormFieldBaseProps } from './field-parts.js';

function ToggleMessages({ id, description, error }: { id: string; description?: ReactNode; error?: string }) {
  return (
    <>
      {description && (
        <p id={`${id}-description`} className="pl-7 text-xs text-text-tertiary">
          {description}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="pl-7 text-xs text-input-error">
          {error}
        </p>
      )}
    </>
  );
}

const describedBy = (id: string, description: unknown, error: unknown) =>
  [description ? `${id}-description` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

/** A yes/no value stated as a sentence ("Require approval before running"). */
export function CheckboxFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  disabled,
  className,
  help,
}: Omit<FormFieldBaseProps<TValues>, 'required'>) {
  const { field, fieldState } = useController<TValues>({ name });
  const id = useId();
  const error = fieldState.error?.message;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-1.5">
        <Checkbox
          name={field.name}
          isSelected={field.value === true}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={fieldState.invalid}
          isDisabled={disabled || field.disabled}
          aria-describedby={describedBy(id, description, error)}
          className="text-sm text-text-primary"
        >
          {label}
        </Checkbox>
        {help && <HelpIcon text={help} />}
      </div>
      <ToggleMessages id={id} description={description} error={error} />
    </div>
  );
}

/** A setting that takes effect as on/off ("Notifications"). */
export function SwitchFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  disabled,
  className,
  help,
}: Omit<FormFieldBaseProps<TValues>, 'required'>) {
  const { field, fieldState } = useController<TValues>({ name });
  const id = useId();
  const error = fieldState.error?.message;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-1.5">
        <Switch
          name={field.name}
          isSelected={field.value === true}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isDisabled={disabled || field.disabled}
          aria-describedby={describedBy(id, description, error)}
          className="group flex cursor-pointer items-center gap-3 text-sm text-text-primary data-disabled:cursor-not-allowed data-disabled:opacity-60"
        >
          <span className="relative h-5 w-9 shrink-0 rounded-full bg-toggle-default-background transition-colors group-data-focus-visible:ring-4 group-data-focus-visible:ring-toggle-active-background/20 group-data-selected:bg-toggle-active-background">
            <span className="absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow-sm transition-transform group-data-selected:translate-x-4" />
          </span>
          {label}
        </Switch>
        {help && <HelpIcon text={help} />}
      </div>
      <ToggleMessages id={id} description={description} error={error} />
    </div>
  );
}
